// AGENT-DASH-KPI: KPI card row.
//
// Renders a row of 6 KPI tiles above the MetricsTable, mirroring the PBI
// "HSQ" page's card-row at the top (visual `10c0fbc7a2eefabc6a8f` and
// neighbours). All values are aggregated from the same /api/dashboard/report
// response the table + charts consume — react-query dedupes the fetch, so
// no extra network round-trip.
//
// Tiles (left → right):
//   1. Won Revenue (12Mo)     — sum of Revenue_12Mo_Calc on pivot rows.
//                               Trend chip = % vs Revenue_Prev12Mo_Calc.
//   2. Prior Revenue (12Mo)   — sum of Revenue_Prev12Mo_Calc (context).
//   3. # Quotes Issued        — sum of IA_QuotesIssued_12Mo (falls back to
//                               response.totals.quotes when missing).
//   4. # Quotes Won (Orders)  — sum of IA_QuotesConverted_12Mo (falls back to
//                               response.totals.orders).
//   5. Win Rate %             — won / issued.
//   6. Avg Order $            — revenue / won-count.
//
// All formatters mirror the MetricsTable rules so the row reads as a natural
// extension of the table totals. Loading / error / empty fall back to em-dash.

import { useMemo } from "react";
import { useReportData } from "../charts/useReportData";
import { useFilterStore } from "../state/filterStore";
import type { PivotRow, ReportResponse } from "../types";
import "./KpiCards.css";

type KpisPayload = NonNullable<ReportResponse["kpis"]> & {
  ordersReleased?: number | null;
  winRateDollar?: number | null;
};

/* ── formatters (kept local — no shared util module owned by this agent) ── */

const FMT_CURRENCY_0 = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const FMT_INT = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const FMT_PCT_1 = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function fmtCurrency(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return "—";
  return FMT_CURRENCY_0.format(n);
}
function fmtInt(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return "—";
  return FMT_INT.format(n);
}
function fmtPct(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return "—";
  return `${FMT_PCT_1.format(n)}%`;
}

/* ── pivot helpers ─────────────────────────────────────────────────────── */

function sumColumn(rows: PivotRow[], col: string): number | null {
  if (!rows.length) return null;
  let saw = false;
  let acc = 0;
  for (const r of rows) {
    const v = r[col];
    if (v === null || v === undefined || v === "") continue;
    const n = typeof v === "number" ? v : Number(String(v).replace(/[,$%\s]/g, ""));
    if (Number.isFinite(n)) {
      saw = true;
      acc += n;
    }
  }
  return saw ? acc : null;
}

/** Find the first matching column name from the candidate list. */
function pickColumn(rows: PivotRow[], candidates: string[]): string | null {
  if (!rows.length) return null;
  const r0 = rows[0];
  for (const c of candidates) {
    if (c in r0) return c;
  }
  // case-insensitive sweep
  const keys = Object.keys(r0);
  for (const c of candidates) {
    const hit = keys.find((k) => k.toLowerCase() === c.toLowerCase());
    if (hit) return hit;
  }
  return null;
}

/* ── derive the six KPIs from the pivot + totals payload ───────────────── */

interface KpiSet {
  revenueWon: number | null;
  revenuePrev: number | null;
  quotesIssued: number | null;
  quotesWon: number | null;
  winRatePct: number | null;
  avgOrder: number | null;
  ordersReleased: number | null;
  /** Trend % delta for Revenue tile (won vs prev). null when not computable. */
  revenueDeltaPct: number | null;
}

function computeRevenueDeltaPct(won: number | null, prev: number | null): number | null {
  if (
    won === null ||
    prev === null ||
    prev === 0 ||
    !Number.isFinite(won) ||
    !Number.isFinite(prev)
  ) {
    return null;
  }
  return ((won - prev) / Math.abs(prev)) * 100;
}

function deriveKpisFromBackend(kpis: KpisPayload): KpiSet {
  const revenueWon = Number.isFinite(kpis.wonRevenue12Mo as number)
    ? (kpis.wonRevenue12Mo as number)
    : null;
  const revenuePrev = Number.isFinite(kpis.priorRevenue12Mo as number)
    ? (kpis.priorRevenue12Mo as number)
    : null;
  const quotesIssued = Number.isFinite(kpis.quotesIssued) ? kpis.quotesIssued : null;
  const quotesWon = Number.isFinite(kpis.quotesWon) ? kpis.quotesWon : null;
  const winRatePct =
    kpis.winRate !== null && Number.isFinite(kpis.winRate) ? kpis.winRate * 100 : null;
  const avgOrder = Number.isFinite(kpis.avgOrderDollar as number)
    ? (kpis.avgOrderDollar as number)
    : null;
  const ordersReleased =
    kpis.ordersReleased !== undefined &&
    kpis.ordersReleased !== null &&
    Number.isFinite(kpis.ordersReleased)
      ? kpis.ordersReleased
      : null;

  return {
    revenueWon,
    revenuePrev,
    quotesIssued,
    quotesWon,
    winRatePct,
    avgOrder,
    ordersReleased,
    revenueDeltaPct: computeRevenueDeltaPct(revenueWon, revenuePrev),
  };
}

function deriveKpisFromPivot(
  rows: PivotRow[],
  totals: { quotes: number; orders: number } | undefined,
): KpiSet {
  const revCol = pickColumn(rows, ["Revenue_12Mo_Calc", "Revenue_12Mo", "Revenue"]);
  const prevCol = pickColumn(rows, ["Revenue_Prev12Mo_Calc", "Revenue_Prev12Mo"]);
  const issuedCol = pickColumn(rows, [
    "IA_QuotesIssued_12Mo",
    "IA_QuotesIssued_Dynamic",
    "QUOTES_ISSUED",
    "RQ",
  ]);
  const convCol = pickColumn(rows, [
    "IA_QuotesConverted_12Mo",
    "IA_QuotesWon_Dynamic",
    "QUOTES_CONVERTED",
    "RP",
  ]);

  const revenueWon = revCol ? sumColumn(rows, revCol) : null;
  const revenuePrev = prevCol ? sumColumn(rows, prevCol) : null;
  let quotesIssued = issuedCol ? sumColumn(rows, issuedCol) : null;
  let quotesWon = convCol ? sumColumn(rows, convCol) : null;

  if (quotesIssued === null && totals && Number.isFinite(totals.quotes)) {
    quotesIssued = totals.quotes;
  }
  if (quotesWon === null && totals && Number.isFinite(totals.orders)) {
    quotesWon = totals.orders;
  }

  let winRatePct: number | null = null;
  if (
    quotesIssued !== null &&
    quotesWon !== null &&
    quotesIssued > 0 &&
    Number.isFinite(quotesIssued) &&
    Number.isFinite(quotesWon)
  ) {
    winRatePct = (quotesWon / quotesIssued) * 100;
  }

  let avgOrder: number | null = null;
  if (revenueWon !== null && quotesWon !== null && quotesWon > 0) {
    avgOrder = revenueWon / quotesWon;
  }

  return {
    revenueWon,
    revenuePrev,
    quotesIssued,
    quotesWon,
    winRatePct,
    avgOrder,
    ordersReleased: null,
    revenueDeltaPct: computeRevenueDeltaPct(revenueWon, revenuePrev),
  };
}

function deriveKpis(
  kpis: KpisPayload | undefined,
  rows: PivotRow[],
  totals: { quotes: number; orders: number } | undefined,
): KpiSet {
  if (kpis) return deriveKpisFromBackend(kpis);
  return deriveKpisFromPivot(rows, totals);
}

/* ── trend chip ────────────────────────────────────────────────────────── */

interface TrendChipProps {
  deltaPct: number | null;
}
function TrendChip({ deltaPct }: TrendChipProps): JSX.Element | null {
  if (deltaPct === null || !Number.isFinite(deltaPct)) return null;
  const up = deltaPct >= 0;
  const cls = up ? "dash-kpi-trend dash-kpi-trend--up" : "dash-kpi-trend dash-kpi-trend--down";
  const arrow = up ? "▲" : "▼";
  // Cap displayed magnitude — fresh data sets can produce huge prev=0-ish ratios.
  const abs = Math.min(Math.abs(deltaPct), 999);
  return (
    <span className={cls} title="vs prior 12 months">
      <span aria-hidden>{arrow}</span>
      <span>{FMT_PCT_1.format(abs)}%</span>
    </span>
  );
}

/* ── tile primitive ────────────────────────────────────────────────────── */

interface KpiTileProps {
  label: string;
  value: string;
  hint?: string;
  trendPct?: number | null;
  /** Hover tooltip explaining a non-obvious metric definition. */
  title?: string;
}
function KpiTile({ label, value, hint, trendPct, title }: KpiTileProps): JSX.Element {
  return (
    <div className="dash-card dash-kpi-tile" role="group" aria-label={label} title={title}>
      <div className="dash-kpi-tile__label">{label}</div>
      <div className="dash-kpi-tile__value">{value}</div>
      <div className="dash-kpi-tile__foot">
        {trendPct !== undefined ? <TrendChip deltaPct={trendPct ?? null} /> : null}
        {hint ? <span className="dash-kpi-tile__hint">{hint}</span> : null}
      </div>
    </div>
  );
}

/* ── exported component ────────────────────────────────────────────────── */

export function KpiCards(): JSX.Element {
  const { data, isLoading, isError } = useReportData();
  const mode = useFilterStore((s) => s.mode);
  const rows = data?.pivot?.rows ?? [];
  const totals = data?.totals;
  const kpis = data?.kpis as KpisPayload | undefined;

  const k = useMemo(() => deriveKpis(kpis, rows, totals), [kpis, rows, totals]);

  const loading = isLoading && rows.length === 0;
  const dash = (s: string) => (loading ? "…" : isError ? "—" : s);

  const avgOrderHint =
    k.ordersReleased !== null
      ? `over ${FMT_INT.format(k.ordersReleased)} orders released`
      : undefined;

  const heading =
    mode === "quotes"
      ? "Quote Performance — 12-Month View"
      : "Order Performance — 12-Month View";

  return (
    <div className="dash-kpi-row" aria-live="polite">
      <div className="dash-kpi-row__heading" aria-label={heading}>
        {heading}
      </div>
      <KpiTile
        label="Won Revenue (12Mo)"
        value={dash(fmtCurrency(k.revenueWon))}
        trendPct={k.revenueDeltaPct}
        title="Released original-order revenue — rolling last 12 months (not fiscal-YTD)"
      />
      <KpiTile
        label="Prior 12Mo Revenue"
        value={dash(fmtCurrency(k.revenuePrev))}
        hint="baseline"
      />
      <KpiTile label="# Quotes Issued" value={dash(fmtInt(k.quotesIssued))} />
      <KpiTile label="# Quotes Won" value={dash(fmtInt(k.quotesWon))} />
      <KpiTile
        label="Win Rate"
        value={dash(fmtPct(k.winRatePct))}
        title="Quotes Won ÷ Quotes Issued (count-based)"
      />
      <KpiTile
        label="Avg Order $"
        value={dash(fmtCurrency(k.avgOrder))}
        hint={avgOrderHint}
      />
    </div>
  );
}

export default KpiCards;
